<template>
	<div>
		<BlockEditorBottomBar
			isPreventSkip
			:isTestingWidget="false"
			:showTestResultModal.sync="isShowTestResultModal"
			:btnDisabled="!isFormValid"
			:testResponse="testResponse"
			:testResultStatus="testResultStatus"
			:showParams="false"
			:isCloudFlow="false"
			:hasEditPermissions="hasEditPermissions"
			:btnFuncLoading="isBtnFuncLoading"
			@btnFunc="btnFunc"
			@skipTest="true"
		/>
		<div class="test-modal narrow-widget-max-width" :class="{ 'is-visible': isShowTestResultModal }">
			<TestResultModal :isError="false" @onClose="isShowTestResultModal = false">
				<TestResultsDataProvider
					:showTestResultModal.sync="isShowTestResultModal"
					:testBtnLoading="isTriggerSetupLoading"
					:testResponseTruncate="testResponseTruncate"
					@test-again="testAndContinue"
				/>
			</TestResultModal>
			<div v-if="loadingTestAndContinue">
				<Loader />
			</div>
		</div>
		<div
			class="editor-overlay"
			:class="{ show: isShowTestResultModal || isShowOverlay || isWaitingForSampleData }"
			@click="isShowTestResultModal = false"
		>
			<Loader />
		</div>
		<slot v-if="isWaitingForSampleData" :cancelCallback="cancelWaitingForSampleData" />
	</div>
</template>

<script>
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';

import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { setWaitingForSampleData } from '@/store/modules/workflows/actions';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { editNode } from '@/store/modules/selectedNode/getters';
import { testStep } from '@/store/modules/selectedNode/actions';

import { getNode, loadingTestAndContinue, getNodeOutputLabelsByNodeId } from '@/store/modules/nodes/getters';
import { rootNode, actionContextNode, elementContextualData } from '@/store/modules/selectedWorkflow/getters';

import { flattenPreserveArrays } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

import BlockEditorBottomBar from '../../BlockEditorBottomBar';
import TestResultModal from '../../TestResultModal';
import TestResultsDataProvider from '../../helpers/TestResultsDataProvider';

import { WAITING_FLAG_TYPES } from '@/utils/workflow-service.const';

export default {
	name: 'BottomSection',
	components: {
		BlockEditorBottomBar,
		TestResultModal,
		TestResultsDataProvider,
	},
	props: {
		projectId: { type: Number },
		currentSubflowId: { type: Number },
		isFormValid: { type: Boolean },
		isOfTypeWaitingForTriggerTest: { type: Boolean },
		isWaitingForSampleData: { type: Boolean },
		testResponseTruncate: { type: Function },
	},
	data() {
		return {
			isShowTestResultModal: false,
			isBtnFuncLoading: false,
			isTriggerSetupLoading: false,
			isShowOverlay: false,
		};
	},
	computed: {
		...mapGettersShorthand({
			editNode,
			editWorkflow,
			loadingTestAndContinue,
			canEditWorkflow,
			elementContextualData,
			actionContextNode,
			getNodeOutputLabelsByNodeId,
			rootNode,
			getNode,
		}),
		testResultStatus() {
			return this.editNodeFromNodesStore?.test_result_status;
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.editWorkflow.id);
		},
		testResponse() {
			if (this.hasContextualDataFromElement) {
				return this.elementContextualData;
			}
			if (this.editNodeFromNodesStore && this.relevantEditNode) {
				return flattenPreserveArrays(this.editNodeTestResponse);
			}
			return {};
		},
		hasContextualDataFromElement() {
			// Don't remove the check for isWebhook ! so we know we are in the rootNode of a contextual action
			// Check that there isn't actionContextNode, and there is elementContextualData data
			return this.isWebhook && !this.actionContextNode && Object.keys(this.elementContextualData).length > 0;
		},
		editNodeTestResponse() {
			const { id: nodeId, test_result: testResult } = this.relevantEditNode;
			const testResponseOutputLabels = this.getNodeOutputLabelsByNodeId(nodeId);
			return WorkflowNodeService.replaceKeysWithOutputLabels(testResult, testResponseOutputLabels);
		},
		relevantEditNode() {
			if (this.isWebhook && this.hasContextualDataFromElement) {
				// In case on context extracted without contextNode - only for isWebhook nodes.
				return this.rootNode;
			}
			return this.editNodeFromNodesStore;
		},
		editNodeFromNodesStore() {
			// This is weird - apparantely editNode doesn't always represent the node in the DB
			return this.getNode(this.editNode.id);
		},
	},
	methods: {
		...mapActionsShorthand({ testStep, setWaitingForSampleData }),
		async btnFunc() {
			if (this.isOfTypeWaitingForTriggerTest) {
				await this.setIsWaitingForSample(true);
			} else {
				this.isBtnFuncLoading = true;
				await this.testStep();
				this.isBtnFuncLoading = false;
				this.isShowTestResultModal = true;
			}
			this.isShowOverlay = false;
		},
		async testAndContinue() {
			this.isShowTestResultModal = false;
			this.isShowOverlay = true;
			this.btnFunc();
		},
		async setIsWaitingForSample(isWaiting) {
			await this.setWaitingForSampleData({
				id: this.editWorkflow.id,
				is_waiting: isWaiting,
				waiting_type: WAITING_FLAG_TYPES.SUB_FLOW_SAMPLE,
			});
		},
		cancelWaitingForSampleData() {
			this.isCanceledWaitingForSampleData = true;
			this.setIsWaitingForSample(false);
			this.isShowOverlay = false;
		},
	},
	watch: {
		isWaitingForSampleData(newVal, oldVal) {
			if (!newVal && oldVal && !this.isCanceledWaitingForSampleData) {
				this.isShowTestResultModal = true;
			}
			this.isCanceledWaitingForSampleData = false;
		},
	},
};
</script>

<style scoped lang="scss">
@import '../../block-editor.scss';
</style>
